import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LoadingSkeleton as Skeleton } from "@/components/ui/loading-skeleton";
import { useStaySchedule } from "@/pages/daycare-hotel/schedule/useStaySchedule";
import { resolveDisplayStatus, type ScheduleReservation } from "@/pages/daycare-hotel/schedule/scheduleModel";
import { CheckInDialog } from "@/pages/daycare-hotel/stay/CheckInDialog";
import { FollowUpDialog } from "@/pages/daycare-hotel/stay/FollowUpDialog";
import type { PanelPet } from "@/pages/daycare-hotel/panel/panelModel";
import type { StayPlanUsage } from "@/hooks/useStayPlanContracts";

export function StayPlanUsageReservationDialog({ usage, open, onOpenChange }: { usage: StayPlanUsage | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation("patients");
  const schedule = useStaySchedule(usage ? { from: usage.usedOn, to: usage.usedOn } : { from: "", to: "" });
  const reservation = useMemo(() => schedule.data?.find((item) => item.id === usage?.stayReservationId) ?? null, [schedule.data, usage?.stayReservationId]);
  const pet = useMemo<PanelPet | null>(() => reservation && usage ? toPanelPet(reservation, usage.usedOn) : null, [reservation, usage]);

  if (!open || !usage) return null;
  if (schedule.isLoading) return <div role="status"><Skeleton className="h-32 w-full" /></div>;
  if (!pet) return <p role="alert" className="text-sm text-muted-foreground">{t("stayPlans.reservationUnavailable")}</p>;
  if (pet.status === "reserved") return <CheckInDialog pet={pet} isoDate={usage.usedOn} open onOpenChange={onOpenChange} />;
  return <FollowUpDialog pet={pet} isoDate={usage.usedOn} open onOpenChange={onOpenChange} />;
}

function toPanelPet(reservation: ScheduleReservation, isoDate: string): PanelPet {
  return {
    reservationId: reservation.id,
    stayDayId: reservation.dayIdByDate?.[isoDate] ?? "",
    petId: reservation.petId,
    petName: reservation.petName,
    ownerId: reservation.ownerId,
    ownerName: reservation.ownerName,
    nature: reservation.nature,
    status: resolveDisplayStatus(reservation, isoDate),
    size: "unknown",
    period: null,
    checkInTime: reservation.checkInTime,
    checkOutTime: null,
    ownerPhone: null,
    isOvernight: reservation.isOvernight,
    inAdaptation: false,
    bedLabel: reservation.bedLabel,
    stayRange: null,
    lastStayDate: reservation.checkOutDate ?? isoDate,
    alerts: [],
    incompatibleWith: [],
    isBirthday: false,
  };
}
